import type {
  SelectedOptions,
  SharedSelection,
} from "@/configurator/domain/models/types";

/**
 * Query parameters that carry a configuration from the desktop widget to a
 * phone (via the AR QR code) and back into the store on the phone.
 *
 * They are prefixed because the widget runs on third-party pages whose own
 * query strings must stay untouched.
 */
export const AR_SHARE_PARAMS = {
  autoStart: "skAr",
  product: "skProduct",
  model: "skModel",
  options: "skOpts",
} as const;

const GROUP_SEPARATOR = "|";
const GROUP_VALUE_SEPARATOR = ":";
const OPTION_SEPARATOR = ",";

function serializeOptions(selectedOptions: SelectedOptions): string {
  return Object.entries(selectedOptions)
    .map(([groupId, value]) => {
      const ids = Array.isArray(value) ? value : [value];
      return `${groupId}${GROUP_VALUE_SEPARATOR}${ids.join(OPTION_SEPARATOR)}`;
    })
    .join(GROUP_SEPARATOR);
}

function parseOptions(raw: string | null): SelectedOptions {
  if (!raw) return {};

  return Object.fromEntries(
    raw
      .split(GROUP_SEPARATOR)
      .map((entry) => {
        const separatorIndex = entry.indexOf(GROUP_VALUE_SEPARATOR);
        if (separatorIndex <= 0) return null;

        const groupId = entry.slice(0, separatorIndex);
        const ids = entry
          .slice(separatorIndex + 1)
          .split(OPTION_SEPARATOR)
          .filter(Boolean);

        // A single id stays a string; a list (including the empty one, which a
        // multi-select group with nothing selected produces) stays a list. Both
        // are re-validated against the catalog before they reach the store.
        return [groupId, ids.length === 1 ? ids[0] : ids] as const;
      })
      .filter((entry): entry is readonly [string, string | string[]] =>
        Boolean(entry),
      ),
  );
}

/**
 * Absolute URL of `pageUrl` carrying the current configuration plus the flag
 * that makes the configurator open AR as soon as the scene is ready.
 */
export function buildARShareUrl(
  pageUrl: string,
  selection: SharedSelection,
): string {
  const url = new URL(pageUrl);

  url.searchParams.set(AR_SHARE_PARAMS.autoStart, "1");
  if (selection.productId) {
    url.searchParams.set(AR_SHARE_PARAMS.product, selection.productId);
  }
  if (selection.modelId) {
    url.searchParams.set(AR_SHARE_PARAMS.model, selection.modelId);
  }

  const options = serializeOptions(selection.selectedOptions);
  if (options) url.searchParams.set(AR_SHARE_PARAMS.options, options);

  return url.toString();
}

export type ARShareLinkParams = {
  // True when the link asks the configurator to launch AR by itself.
  autoStartAR: boolean;
  selection: SharedSelection;
};

/** Reads the share parameters out of a `location.search` string. */
export function parseARShareParams(search: string): ARShareLinkParams {
  const params = new URLSearchParams(search);

  return {
    autoStartAR: params.get(AR_SHARE_PARAMS.autoStart) === "1",
    selection: {
      productId: params.get(AR_SHARE_PARAMS.product) ?? undefined,
      modelId: params.get(AR_SHARE_PARAMS.model) ?? undefined,
      selectedOptions: parseOptions(params.get(AR_SHARE_PARAMS.options)),
    },
  };
}
